import { useEffect, useMemo } from 'react';
import { Crown, Layers, Target } from 'lucide-react';
import { ControlButton } from '../ControlButton';
import { TrinityPanel } from '../trinity/TrinityPanel';
import { useAutoCenterPreference } from '../../hooks/useAutoCenterPreference';
import { useWorkspaceStore } from '@/store/workspaceStore';
import type { AutoCenterMode } from '@/lib/autoCenterPreference';
import type { SupportedSymbol } from '@/lib/symbols';

const TAB_BRAND = 'Meridian View AI';

interface TrinityViewProps {
  symbols: readonly SupportedSymbol[];
  visibleStrikeCount?: number | null;
}

export function TrinityView({ symbols, visibleStrikeCount }: TrinityViewProps) {
  const trinityPanels = useWorkspaceStore((state) => state.trinityPanels);
  const setTrinityPanelTicker = useWorkspaceStore((state) => state.setTrinityPanelTicker);
  const setTrinityPanelType = useWorkspaceStore((state) => state.setTrinityPanelType);
  const { autoCenterMode, autoCenterPreferenceReady, updateAutoCenterMode } =
    useAutoCenterPreference();

  const panelTickers = useMemo(() => trinityPanels.map((panel) => panel.ticker), [trinityPanels]);

  useEffect(() => {
    document.title = `${panelTickers.join(' / ')} | ${TAB_BRAND}`;
  }, [panelTickers]);

  const toggleAutoCenter = (mode: Exclude<AutoCenterMode, 'off'>) => {
    updateAutoCenterMode(autoCenterMode === mode ? 'off' : mode);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-zinc-800 bg-background px-3 py-2 min-[1024px]:px-6">
        <Layers className="h-3.5 w-3.5 text-zinc-400" />
        <span className="font-mono text-[12px] font-bold tracking-[0.08em] text-neutral-100">
          TRINITY
        </span>
        <span className="font-mono text-[11px] text-neutral-500">
          {trinityPanels.length} panels
        </span>
        <div className="ml-auto flex items-center gap-1">
          <ControlButton
            icon={Target}
            title={
              autoCenterMode === 'spot' ? 'Turn off auto-center on spot' : 'Auto-center on spot'
            }
            isActive={autoCenterMode === 'spot'}
            disabled={!autoCenterPreferenceReady}
            onClick={() => toggleAutoCenter('spot')}
          />
          <ControlButton
            icon={Crown}
            title={
              autoCenterMode === 'king' ? 'Turn off auto-center on king' : 'Auto-center on king'
            }
            isActive={autoCenterMode === 'king'}
            disabled={!autoCenterPreferenceReady}
            onClick={() => toggleAutoCenter('king')}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2 min-[900px]:flex-row min-[900px]:gap-3 min-[900px]:overflow-y-visible min-[900px]:p-3 min-[1024px]:px-6">
        {trinityPanels.map((panel, panelIndex) => (
          <TrinityPanel
            key={panelIndex}
            panel={panel}
            symbols={symbols}
            autoCenterMode={autoCenterMode}
            visibleStrikeCount={visibleStrikeCount}
            onTickerChange={(ticker) => setTrinityPanelTicker(panelIndex, ticker)}
            onTypeChange={(type) => setTrinityPanelType(panelIndex, type)}
          />
        ))}
      </div>
    </div>
  );
}
